<script setup lang="ts">
import RlDialog from '@/components/common/RlDialog.vue'
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import { usePolicies } from '@/composables/asicw/policies'
import { maskForm } from '@/helpers'
import {
  CoolingMethod,
  Environment,
  PolicyStatus,
  type IPolicy,
  type IPolicyBulkUpdateForm,
} from '@/types/asicw/policies'
import { reduce, values } from 'lodash'
import { computed, reactive, ref } from 'vue'

// Interface
const props = defineProps<{ policies: IPolicy[] }>()
const open = defineModel<boolean>({ required: true })

const { bulkUpdatePoliciesMutation, getSiteOptionsQuery } = usePolicies()

// Sites query to populate site options
const filters = reactive({})
const {
  data: siteOptions,
  isError: isQueryError,
  error: queryError,
  isPending: isSitesQueryPending,
} = getSiteOptionsQuery(filters, open)

// Rest of select options
const statusOptions = Object.values(PolicyStatus)
const environmentOptions = Object.values(Environment)
const coolingMethodOptions = Object.values(CoolingMethod)
const booleanOptions = ['Yes', 'No']

// Form state

const anySelected = computed(() => Object.values(isActive).some((isSet) => isSet)) // At least one selected

const allSelectedAreSet = computed(() => {
  const setValues = values(maskForm(bulkChangeForm, isActive))
  // Every active field has a value set, and there's at least one
  return setValues.length > 0 && reduce(setValues, (acc, value) => acc && value != undefined, true)
})

const policyIds = computed(() => props.policies.map((policy) => policy.id))

const formInitialState = (): Omit<IPolicyBulkUpdateForm, 'policy_ids'> =>
  structuredClone({
    sale_price: undefined,
    status: undefined,
    site_id: undefined,
    facility_type: undefined,
    cooling_type: undefined,
    third_party_firmware_used: undefined,
    overclocking: undefined,
  })

const bulkChangeForm = reactive(formInitialState())

const activeInitialState = () =>
  structuredClone({
    sale_price: false,
    status: false,
    site_id: false,
    facility_type: false,
    cooling_type: false,
    third_party_firmware_used: false,
    overclocking: false,
  })

const isActive = reactive(activeInitialState())

const onClosed = () => {
  Object.assign(bulkChangeForm, formInitialState())
  Object.assign(isActive, activeInitialState())
  isBulkUpdatePending.value = false
}

// BE call

const isBulkUpdatePending = ref(false)
const { mutate, isError: isUpdateError, error: updateError, isPending: isUpdatePending } = bulkUpdatePoliciesMutation

const applyBulkPolicyChanges = () => {
  if (!policyIds.value) return
  isBulkUpdatePending.value = true
  mutate(
    { policy_ids: policyIds.value, ...maskForm(bulkChangeForm, isActive) },
    {
      onSuccess: () => {
        open.value = false
      },
      onError: () => {
        isBulkUpdatePending.value = false
      },
    }
  )
}
</script>

<template>
  <RlDialog v-model="open" title="Bulk policy changes" :width="544" @closed="onClosed">
    <div class="w-full p-6 flex flex-col gap-4">
      <p class="text-left">
        Make bulk changes to selected {{ policies.length }} {{ policies.length == 1 ? 'policy' : 'policies' }}
      </p>
      <div class="grid grid-cols-[15px_140px_1fr] w-full pt-4 gap-4 items-center">
        <!---------------- Sale price ------------------>
        <el-checkbox v-model="isActive.sale_price" />

        <!-- Label spans two columns if we're not rendering the select -->
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.sale_price }]">Sale price</label>

        <el-currency-input v-if="isActive.sale_price" v-model="bulkChangeForm.sale_price" clearable />

        <!---------------- Status ------------------>
        <el-checkbox v-model="isActive.status" />

        <!-- Label spans two columns if we're not rendering the select -->
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.status }]">Status</label>

        <!-- Select only when the field's active -->
        <el-select
          v-if="isActive.status"
          v-model="bulkChangeForm.status"
          placeholder="Set status"
          clearable
          class="w-full"
        >
          <el-option v-for="option in statusOptions" :key="option" :value="option" :label="option" />
        </el-select>

        <!---------------- Location ------------------->
        <el-checkbox v-model="isActive.site_id" />
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.site_id || !siteOptions }]">Location</label>
        <el-select
          v-if="isActive.site_id && siteOptions"
          v-model="bulkChangeForm.site_id"
          placeholder="Set location"
          :disabled="isSitesQueryPending"
          clearable
          class="w-full"
        >
          <el-option
            v-for="option in siteOptions.data"
            :key="option.site_id"
            :value="option.site_id"
            :label="option.site_name"
          />
        </el-select>

        <!------------- Environment ----------------->
        <el-checkbox v-model="isActive.facility_type" />
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.facility_type }]">Environment</label>
        <el-select
          v-if="isActive.facility_type"
          v-model="bulkChangeForm.facility_type"
          placeholder="Set environment"
          clearable
          class="w-full"
        >
          <el-option v-for="option in environmentOptions" :key="option" :value="option" :label="option" />
        </el-select>

        <!------------- Cooling method -------------->
        <el-checkbox v-model="isActive.cooling_type" />
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.cooling_type }]">Cooling method</label>
        <el-select
          v-if="isActive.cooling_type"
          v-model="bulkChangeForm.cooling_type"
          placeholder="Set cooling method"
          clearable
          class="w-full"
        >
          <el-option v-for="option in coolingMethodOptions" :key="option" :value="option" :label="option" />
        </el-select>

        <!----------- 3rd party software ------------>
        <el-checkbox v-model="isActive.third_party_firmware_used" />
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.third_party_firmware_used }]"
          >3rd party software</label
        >
        <el-select
          v-if="isActive.third_party_firmware_used"
          v-model="bulkChangeForm.third_party_firmware_used"
          placeholder="Running 3rd party software?"
          clearable
          class="w-full"
        >
          <el-option v-for="option in booleanOptions" :key="option" :value="option == 'Yes'" :label="option" />
        </el-select>

        <!-------------- Overclocking ---------------->
        <el-checkbox v-model="isActive.overclocking" />
        <label :class="['whitespace-nowrap', { 'col-span-2': !isActive.overclocking }]">Overclocking</label>
        <el-select
          v-if="isActive.overclocking"
          v-model="bulkChangeForm.overclocking"
          placeholder="Overclocking miners?"
          clearable
          class="w-full"
        >
          <el-option v-for="option in booleanOptions" :key="option" :value="option == 'Yes'" :label="option" />
        </el-select>
      </div>
      <RlErrorAlert v-if="isQueryError && !isSitesQueryPending" :error="queryError" />
      <RlErrorAlert v-if="isUpdateError && !isUpdatePending" :error="updateError" />
    </div>
    <template #footer>
      <el-button size="large" @click="open = false">Cancel</el-button>
      <el-button
        size="large"
        type="primary"
        :disabled="!anySelected || !allSelectedAreSet"
        :loading="isBulkUpdatePending"
        @click="applyBulkPolicyChanges"
        >Apply</el-button
      >
    </template>
  </RlDialog>
</template>
